// PeakEdu Tier 0 WASM — Cookie consent banner skeleton (B18 SD44 PM)
//
// Hỗ trợ GDPR + ePrivacy + NĐ 13/2023 cookie compliance.
// Tham chiếu: docs/legal/cookie-policy-vn.md § 2 (4 categories: necessary/analytics/marketing/functional)
//
// Public API:
//   initCookieConsent({legalBaseUrl, onChange})  — inject banner + auto-show nếu chưa consent
//   getCookieConsent()                            — read current state {choices, version, timestamp}
//   setCookieConsent(choices)                     — programmatic set (e.g. dialog "Cài đặt cookie")
//   resetCookieConsent()                          — clear consent + re-show banner
//
// Trạng thái lưu localStorage key `peakedu_consent`:
//   {
//     version: "1.0",
//     timestamp: "2026-05-07T12:00:00Z",
//     choices: { necessary: true, analytics: false, marketing: false, functional: false }
//   }
//
// Default: necessary=true (always), analytics/marketing/functional=false.
// Banner hiển thị khi:
//   - Chưa có consent record
//   - Version mismatch (Cookie Policy thay đổi)
//   - Timestamp >12 tháng (re-prompt)

const STORAGE_KEY = 'peakedu_consent';
const CURRENT_VERSION = '1.0';
const TWELVE_MONTHS_MS = 365 * 24 * 60 * 60 * 1000;

const DEFAULT_CHOICES = {
  necessary: true,    // always — dịch vụ thiết yếu
  analytics: false,
  marketing: false,
  functional: false,
};

/**
 * Read consent state từ localStorage.
 * Returns null nếu không có hoặc parse fail.
 */
export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || !parsed.choices) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persist consent state.
 * @param {Partial<typeof DEFAULT_CHOICES>} choices
 */
export function setCookieConsent(choices) {
  const merged = { ...DEFAULT_CHOICES, ...choices, necessary: true };
  const record = {
    version: CURRENT_VERSION,
    timestamp: new Date().toISOString(),
    choices: merged,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage có thể disabled (private browsing, quota) — silent fail
  }
  return record;
}

/**
 * Clear consent + return promise resolved sau khi banner re-show.
 */
export function resetCookieConsent() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Determine xem banner có cần show không.
 */
function _shouldShowBanner() {
  const record = getCookieConsent();
  if (!record) return true;
  if (record.version !== CURRENT_VERSION) return true;
  try {
    const ts = new Date(record.timestamp).getTime();
    if (Number.isFinite(ts) && Date.now() - ts > TWELVE_MONTHS_MS) {
      return true;
    }
  } catch {
    return true;
  }
  return false;
}

/**
 * Inject banner DOM + return root element + dispose handle.
 *
 * @param {object} opts
 * @param {string} opts.legalBaseUrl  — Base URL cho `/chinh-sach-cookie/`
 * @param {(record) => void} opts.onChange  — Callback khi user lưu consent
 */
function _injectBanner({ legalBaseUrl, onChange }) {
  const root = document.createElement('div');
  root.id = 'peakedu-cookie-banner';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-label', 'Đồng ý cookie');
  root.setAttribute('aria-live', 'polite');
  root.style.cssText = `
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #1a365d;
    color: #fff;
    padding: 1rem 1.25rem;
    box-shadow: 0 -4px 16px rgba(0,0,0,0.15);
    z-index: 99999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 0.9rem;
    line-height: 1.5;
  `;

  const cookiePolicyUrl = `${legalBaseUrl.replace(/\/$/, '')}/chinh-sach-cookie/`;

  root.innerHTML = `
    <div style="max-width:880px;margin:0 auto;display:flex;flex-direction:column;gap:0.75rem;">
      <div>
        <strong>🍪 PeakEdu sử dụng cookie</strong> để bảo đảm hoạt động Dịch vụ và (nếu bạn đồng ý) cải thiện trải nghiệm qua phân tích ẩn danh.
        <a href="${cookiePolicyUrl}" target="_blank" rel="noopener noreferrer" style="color:#90cdf4;text-decoration:underline;">Tìm hiểu thêm</a>
      </div>
      <details style="background:rgba(255,255,255,0.06);padding:0.5rem 0.75rem;border-radius:6px;">
        <summary style="cursor:pointer;user-select:none;color:#cbd5e0;font-size:0.85rem;">Tuỳ chỉnh chi tiết</summary>
        <div style="margin-top:0.5rem;display:flex;flex-direction:column;gap:0.25rem;font-size:0.85rem;">
          <label style="display:flex;align-items:center;gap:0.5rem;opacity:0.6;">
            <input type="checkbox" checked disabled />
            <span><strong>Cần thiết</strong> — luôn bật (session, JWT, CSRF)</span>
          </label>
          <label style="display:flex;align-items:center;gap:0.5rem;cursor:pointer;">
            <input type="checkbox" id="cc-analytics" />
            <span><strong>Phân tích</strong> — Plausible Analytics ẩn danh (no fingerprint, no cookie cross-site)</span>
          </label>
          <label style="display:flex;align-items:center;gap:0.5rem;opacity:0.6;">
            <input type="checkbox" disabled />
            <span><strong>Marketing</strong> — không sử dụng (xem Cookie Policy § 2.3)</span>
          </label>
        </div>
      </details>
      <div style="display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:flex-end;">
        <button type="button" id="cc-accept-necessary" style="
          padding:0.5rem 1rem;background:transparent;color:#fff;
          border:1px solid #4a5568;border-radius:6px;cursor:pointer;font-size:0.85rem;
        ">Chỉ cần thiết</button>
        <button type="button" id="cc-save-custom" style="
          padding:0.5rem 1rem;background:#4a5568;color:#fff;
          border:none;border-radius:6px;cursor:pointer;font-size:0.85rem;
        ">Lưu lựa chọn</button>
        <button type="button" id="cc-accept-all" style="
          padding:0.5rem 1.25rem;background:#3182ce;color:#fff;
          border:none;border-radius:6px;cursor:pointer;font-size:0.9rem;font-weight:600;
        ">Chấp nhận tất cả</button>
      </div>
    </div>
  `;

  document.body.appendChild(root);

  function dismiss(record) {
    try {
      root.remove();
    } catch {
      /* ignore */
    }
    if (typeof onChange === 'function') {
      try {
        onChange(record);
      } catch {
        /* ignore */
      }
    }
  }

  root.querySelector('#cc-accept-all').addEventListener('click', () => {
    const r = setCookieConsent({ analytics: true, marketing: false, functional: true });
    dismiss(r);
  });

  root.querySelector('#cc-accept-necessary').addEventListener('click', () => {
    const r = setCookieConsent({ analytics: false, marketing: false, functional: false });
    dismiss(r);
  });

  root.querySelector('#cc-save-custom').addEventListener('click', () => {
    const analytics = root.querySelector('#cc-analytics').checked;
    const r = setCookieConsent({ analytics, marketing: false, functional: false });
    dismiss(r);
  });

  return root;
}

/**
 * Init cookie banner — auto-show nếu chưa consent / version mismatch / >12 tháng.
 * Chạy idempotent: gọi nhiều lần không inject duplicate.
 *
 * @param {object} opts
 * @param {string} opts.legalBaseUrl  — Base URL cho legal pages
 * @param {(record) => void} [opts.onChange]
 */
export function initCookieConsent({ legalBaseUrl, onChange } = {}) {
  // Idempotent — nếu banner đã exist, skip
  if (document.getElementById('peakedu-cookie-banner')) {
    return;
  }
  if (!_shouldShowBanner()) {
    // Existing valid consent — không show banner, optionally fire onChange với current
    const record = getCookieConsent();
    if (typeof onChange === 'function' && record) {
      try {
        onChange(record);
      } catch {
        /* ignore */
      }
    }
    return;
  }
  _injectBanner({
    legalBaseUrl: legalBaseUrl || 'https://peakedu.vn',
    onChange,
  });
}

// Default export object cho convenient destructure
export default {
  init: initCookieConsent,
  get: getCookieConsent,
  set: setCookieConsent,
  reset: resetCookieConsent,
};
